import { flow, getParent, types as t } from 'mobx-state-tree';

import { toQueryString } from '@utils/requests';

import { Application } from 'models/applications';
import { LoadingState } from 'models/ui';
import { withEnv } from 'models/withEnv';

export const buildOrderString = (order) => {
  const [prop, ascdesc] = order.split('-');
  let str = ascdesc === 'desc' ? '-' : '';
  switch (prop) {
    case 'votes':
      return str + 'num_rating';
    case 'avgvote':
      return str + 'rating';
    case 'notes':
      return str + 'num_notes';
    default:
      return str + prop;
  }
};

const DynamicStages = t.model('DynamicStages', {
  stage: t.number,
  autoOpenModal: t.enumeration('Modal', [
    'ASSIGN_APPLICANT',
    'HIRE_APPLICANT',
    'REJECT_APPLICANTS',
  ]),
});

export const JobStage = t
  .model('JobStage', {
    id: t.identifierNumber,
    name: t.string,
    order: t.number,
    isEntryStage: false,
    dynamicStage: t.array(DynamicStages),

    // Local state
    applications: t.array(t.reference(t.late(() => Application))),
    isRenaming: false,
    isNew: false,
    next: t.maybeNull(t.string),
    state: t.optional(LoadingState, 'init'),
    nextState: t.optional(LoadingState, 'init'),
    count: 0,
  })
  .views(withEnv)
  .views((self) => ({
    get rejectableApplications() {
      return self.applications.filter((a) => !a.isHired && !a.isRetracted);
    },
    get activeApplications() {
      return self.applications.filter((a) => a.status === 'active');
    },
    get hiredApplications() {
      return self.applications.filter((a) => a.isHired);
    },
    get job() {
      return getParent(self, 2);
    },
    get stageSettings() {
      return self.job.settings.pipeline.get(self.id);
    },
    get sort() {
      return self.stageSettings ? self.stageSettings.sort : '';
    },
  }))
  .actions((self) => ({
    setIsNew(value) {
      self.isNew = value;
    },
    setOrder(newOrder) {
      self.order = newOrder;
    },
    removeApplication(id) {
      const idx = self.applications.findIndex((a) => a.id === id);
      if (idx !== -1) {
        self.count -= 1;
        self.applications.splice(idx, 1);
      }
    },
    prependApplication(id) {
      if (!self.applications.find((a) => a.id === id)) {
        self.count += 1;
        self.applications.unshift(id);
      }
    },
    addApplication(id) {
      if (!self.applications.find((a) => a.id === id)) {
        self.count += 1;
        self.applications.push(id);
      }
    },
    setSort(sort) {
      self.job.settings.setApplicationSort(self.id, sort);
      self.load(true);
    },
    setIsRenaming(value) {
      self.isRenaming = value;
    },
    setName(name) {
      self.name = name;
    },
    reset() {
      self.applications.clear();
      self.state = 'init';
      self.nextState = 'init';
      self.next = null;
      self.count = 0;
    },
    setFromResponseData(data, clear = false) {
      if (clear) {
        self.reset();
      }
      let appIds = [];

      data.results.forEach((a) => {
        // @Migration: Remove this spread when the api stops sending
        // chat, attachments, messages and reviews.
        const { chat, attachments, messages, reviews, ...rest } = a;

        if (!self.env.applicationStore.items.has(a.id)) {
          appIds.push(a.id);
        }

        self.env.applicationStore.addOrUpdate(rest, self.job.id);
        self.addApplication(a.id);
      });
      self.count = data.count;
      self.next = data.next;
    },
    loadNext: flow(function* () {
      if (self.nextState === 'loading') return;
      self.nextState = 'loading';
      const res = yield self.env.api.global.get({ url: self.next });
      if (!res.error) {
        self.setFromResponseData(res.data);
        self.nextState = 'loaded';
      } else {
        self.nextState = 'error';
      }
    }),
    reload: flow(function* () {
      yield self.load(true);
    }),
    loadWithoutFilters: flow(function* () {
      self.state = 'loading';

      const query = toQueryString({
        limit: 50,
        ordering: buildOrderString(self.sort),
        fixed__job: self.job.id,
        fixed__job_stage_id: self.id,
      });

      try {
        const data = yield self.env.api.applications.search(query);
        self.setFromResponseData(data, true);
        self.state = 'loaded';
      } catch (e) {
        self.state = 'error';
      }
    }),
    load: flow(function* (clear = false, limit = 20) {
      self.state = 'loading';
      const { data: queryData, conjunction } = self.job.getSearchQueryData();

      const method =
        conjunction === 'and'
          ? self.env.api.applications.search
          : self.env.api.applications.orSearch;

      const query = toQueryString({
        limit: limit,
        ordering: buildOrderString(self.sort),
        fixed__job: self.job.id,
        fixed__job_stage_id: self.id,
        ...queryData,
      });

      try {
        const data = yield method(query);
        self.setFromResponseData(data, clear);
        self.state = 'loaded';
      } catch (e) {
        self.state = 'error';
      }
    }),
    async loadAll(clear = false) {
      await self.load(clear, 10000);
    },
  }));
